import { useState } from 'react'
import { ChevronDown, X, Search } from 'lucide-react'
import { Button } from './ui/button'
import { Label } from './ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Checkbox } from './ui/checkbox'
import { Input } from './ui/input'
export interface MultiSelectOption {
  id: string
  name: string
  title: string
}
interface MultiSelectProps {
  label: string
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selectedIds: string[]) => void
  placeholder?: string
  required?: boolean
  className?: string // allow custom className for trigger
  labelClassName?: string // allow custom className for trigger
  children?: React.ReactNode // allow custom elements below the dropdown
  disabledOptions?: string[] // ids of options to disable
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  required = false,
  className = '',
  labelClassName = '',
  children,
  disabledOptions = [],
}: Readonly<MultiSelectProps>) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  // Filter options based on search
  const filteredOptions = searchTerm.trim()
    ? options.filter(o =>
        o.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options

  const handleToggle = (value: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const removeItem = (value: string, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onChange(selected.filter(v => v !== value))
  }
  const selectedOptions = options.filter(option => selected.includes(option.id))
  return (
    <div className="space-y-2">
      <Label
        className={`text-sm leading-6 font-medium text-secondary-foreground ${labelClassName}`}
      >
        <div className="flex justify-between">
          {label}
          {children}
        </div>
        {required && <span className="text-red-600 text-xs">*</span>}
      </Label>

      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="medium"
            className={`w-full justify-between px-3 bg-ss-neutral-white border border-ss-neutral-200 text-muted-foreground hover:bg-gray-50 h-9 ${className}`}
          >
            {selected.length > 0 ? `${selected.length} selected` : placeholder}
            <ChevronDown className="h-4 w-4 text-ss-neutral-200" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="max-h-80 overflow-hidden p-0 w-[230px] md:w-[370px]"
          align="start"
          onCloseAutoFocus={e => e.preventDefault()}
        >
          {/* Search Input */}
          <div className="relative p-3 pb-1">
            <Search className="absolute left-5 top-7 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder={`Search ${label.toLowerCase()}`}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              inputSize="medium"
              className="pl-7 pr-3 w-full h-8"
              onClick={e => e.stopPropagation()}
              onKeyDownCapture={e => e.stopPropagation()} // 👈 prevents Radix key handling
            />
          </div>

          {/* Options */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => {
                const isDisabled = disabledOptions?.includes(option.id)
                return (
                  <DropdownMenuItem
                    key={option.id}
                    onClick={
                      isDisabled ? undefined : e => handleToggle(option.id, e)
                    }
                    className={`flex items-center space-x-2 cursor-pointer px-3 py-2 ${isDisabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}
                    onSelect={e => e.preventDefault()}
                    disabled={isDisabled}
                  >
                    <Checkbox
                      checked={selected.includes(option.id)}
                      className="border-ss-neutral-200"
                      disabled={isDisabled}
                    />
                    <span className="flex-1 text-sm truncate max-w-[calc(100%-20px)]">
                      {option.title}
                    </span>
                  </DropdownMenuItem>
                )
              })
            ) : (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {searchTerm.trim()
                  ? `No ${label.toLowerCase()} found`
                  : `No ${label.toLowerCase()} available`}
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Selected chips */}
      {selectedOptions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedOptions.map(item => (
            <div
              key={item.id}
              className="inline-flex items-center gap-1 px-2 py-1 border border-ss-neutral-950 text-ss-neutral-950 text-xs font-medium rounded-full"
            >
              <span>{item.title}</span>
              <button
                onClick={e => removeItem(item.id, e)}
                className="hover:bg-blue-100 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
